import { HttpClient, HttpEventType } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { Product } from '@infrab4a/connect';
import { FileUpload } from 'primeng/fileupload';
import { forkJoin, lastValueFrom, map, switchMap } from 'rxjs';
import { UserControllerService } from 'src/app/admin-api';
import { ShopLoggingService } from 'src/app/connect-api/api/shop/shop-log-service';
import { ShopProductService } from 'src/app/connect-api/api/shop/shop-product.service';
import { LoaderService } from 'src/app/services/loader.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-product-batch-images',
  templateUrl: './batch-images.component.html',
  styleUrl: './batch-images.component.scss'
})
export class BatchImagesComponent {
  @ViewChild('imagesUpload') imagesUpload: FileUpload;
  selectedFiles: File[] | null = [];
  productsNotFound: string[] = [];
  imagesPreview: Array<{
    ean: string;
    id: string;
    name: string;
    isVariant: boolean;
    valid?: boolean;
    images: {
      file: File;
      itemImageSrc: string;
      thumbnailImageSrc: string;
      alt: string;
      title: string;
    }[];
  }> = [];
  displayCustom: boolean | undefined;
  activeIndex: number = 0;
  uploadFilesProgress: number[] = [];
  uploadProgress = null;
  isUploading = false;

  constructor(
    private productService: ShopProductService,
    private http: HttpClient,
    private userService: UserControllerService,
    private shopLoggingService: ShopLoggingService
  ) {}

  onFileSelected(event: any): void {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (event.currentFiles) {
      for (const file of event.currentFiles as File[]) {
        if (!allowedTypes.includes(file.type)) {
          throw new Error(`Imagem fora do formato permitido: ${file.name}`);
        }
      }

      this.selectedFiles = event.currentFiles;
      this.validateInputImages(this.selectedFiles).then(() =>
        this.validateOrder()
      );
    }
  }

  upload(): void {
    const images = this.imagesPreview
      .filter((product) => product.valid)
      .map((img) => img.images)
      .flat();

    if (images.length) {
      LoaderService.showLoader();
      this.uploadProgress = 0;
      this.isUploading = true;
      const files = images.map((image) => image.file);

      this.uploadImages(files).subscribe({
        next: (response) => {
          console.log(response);
        },
        error: (err) => {
          console.error('Erro no upload:', err);
          LoaderService.showLoader(false);
        },
        complete: async () => {
          this.isUploading = false;
          const documentsUpdated = this.imagesPreview
            .filter((product) => product.valid)
            .map((product) => {
              return Product.toInstance({
                id: product.id,
                EAN: product.ean,
                images: product.images.map((img) => img.file.name)
              });
            })
            .flat();

          await this.createLog(documentsUpdated);
          LoaderService.showLoader(false);
        }
      });
    }
  }

  uploadImages(files: File[]) {
    const signedUrl = `https://southamerica-east1-${environment.connectFirebase.projectId}.cloudfunctions.net/productImagesCreateSignUrl`;
    const fileNames = Array.from(files).map((file) => file.name);
    const totalFiles = files.length;
    return this.http
      .post<{ fileName: string; signedUrl: string }[]>(signedUrl, { fileNames })
      .pipe(
        switchMap((signedUrlResponses) => {
          const uploadObservables = signedUrlResponses.map(
            (response, index) => {
              const fileToUpload = Array.from(files).find(
                (file) => file.name === response.fileName
              );
              return this.http
                .put(response.signedUrl, fileToUpload, {
                  headers: {
                    'Content-Type': fileToUpload.type
                  },
                  reportProgress: true,
                  observe: 'events'
                })
                .pipe(
                  map((event: any) => {
                    if (event.type === HttpEventType.UploadProgress) {
                      const progress = Math.round(
                        (100 * event.loaded) / event.total
                      );
                      this.uploadFilesProgress[index] = progress;
                      this.uploadProgress =
                        this.uploadFilesProgress.reduce((a, b) => {
                          return a + b;
                        }, 0) / totalFiles;

                      return progress;
                    }
                    return 0;
                  })
                );
            }
          );
          return forkJoin(uploadObservables);
        })
      );
  }

  private async validateInputImages(images: any[]) {
    LoaderService.showLoader();
    const orderedImages = images.sort((a, b) => a.name.localeCompare(b.name));

    for (const image of orderedImages) {
      const ean = image.name.split('_').at(0);
      const index = this.imagesPreview.findIndex((p) => p.ean == ean);

      if (index != -1) {
        this.imagesPreview[index].images.push({
          itemImageSrc: image.objectURL,
          thumbnailImageSrc: image.objectURL,
          alt: this.imagesPreview[index].name,
          title: this.imagesPreview[index].name,
          file: image
        });
      } else {
        if (this.productsNotFound.some((p) => p === ean)) continue;

        const product = await this.productService.getProductOrVariantByEan(ean);

        if (!product) {
          this.productsNotFound.push(ean);
          continue;
        }

        this.imagesPreview.push({
          id: product.id,
          ean: product.EAN,
          name: product.name,
          isVariant: product.isVariant,
          valid: true,
          images: [
            {
              itemImageSrc: image.objectURL,
              thumbnailImageSrc: image.objectURL,
              alt: product.name,
              title: product.EAN,
              file: image
            }
          ].sort((a, b) => a.file.name.localeCompare(b.file.name))
        });
      }
    }
    LoaderService.showLoader(false);
  }

  private validateOrder() {
    for (const [index, product] of this.imagesPreview.entries()) {
      const numeracao = this.getNumeracaoProduto(product.images);

      if (!this.hasNumercaoCorreta(numeracao)) {
        this.imagesPreview[index].valid = false;
      }
    }

    return;
  }

  private getNumeracaoProduto(images: Array<{ file: { name: string } }>) {
    const numeracao: number[] = [];

    for (const image of images) {
      const [, ordem] = image.file.name.split('_');
      numeracao.push(parseInt(ordem));
    }

    return numeracao;
  }

  private hasNumercaoCorreta(numeracoes: number[]) {
    for (let i = 0; i < numeracoes.length; i++) {
      if (numeracoes[i] !== i + 1) {
        return false;
      }
    }

    return true;
  }

  clearBatchUpdate() {
    this.selectedFiles = [];
    this.productsNotFound = [];
    this.imagesPreview = [];
    this.uploadFilesProgress = [];
    this.uploadProgress = null;
    this.isUploading = false;
    this.imagesUpload.clear();
  }

  imageClick(index: number) {
    this.activeIndex = index;
    this.displayCustom = true;
  }

  async createLog(documents: any[]) {
    const user = await lastValueFrom(
      this.userService.getInfo().pipe(map((data) => data.result))
    );

    return this.shopLoggingService.createBatchUpdateLog('products', documents, {
      id: user.userId.toString(),
      name: user.displayName,
      email: user.username
    });
  }

  get canUpload(): boolean {
    return Boolean(this.validProducts.map((img) => img.images).flat().length);
  }

  get validProducts() {
    return this.imagesPreview.filter((product) => product.valid);
  }

  get invalidProducts() {
    return this.imagesPreview.filter((product) => !product.valid);
  }
}
